import {
  SubscriptionData,
  SubscriptionStatus,
  SubscriptionInfo,
} from '../interfaces/subscription';
import { AppRoutes } from '../constants/route';
import { CreditBalance } from '../hooks/useCreditBalance';
import { formatDate } from './timeUtils';
import { Ability } from '../services/abilityService';
import { Resource } from '../enums/common';
import { convertSubscriptionDataToStripeSubscription } from '../services/subscription';
import { Team, TeamUser } from '../interfaces/team';
import { getTeamName } from '../services/account.service';
import { PersonalUser } from '../interfaces/account';

export interface CreditButtonInfo {
  ability: Ability;
  text: string;
  style: React.CSSProperties;
  url: string;
  useSubscriptionHandler: boolean;
  buttonDisabled?: boolean;
  buttonTooltip?: string | null;
  buttonHidden?: boolean;
  instructionForSubscription?: string | null;
}

type InternalCreditButtonInfo = Omit<
  CreditButtonInfo,
  'ability' | 'buttonDisabled' | 'buttonTooltip' | 'buttonHidden'
>;

const _getCreditButtonInfo = (
  balanceInfo: CreditBalance,
  subscription: SubscriptionData | null
): InternalCreditButtonInfo => {
  const { isLoading, isRunningOutOfCredits, hasAdditionalCredits } =
    balanceInfo;
  if (isLoading || !subscription) {
    return {
      text: 'Purchase credits',
      style: {},
      url: AppRoutes.buyCredits.path,
      useSubscriptionHandler: false,
    };
  }

  // Check if user is on free plan
  const isFreePlan =
    subscription.status === SubscriptionStatus.FREE ||
    subscription.freePlan === true;

  // Check if user is on trial
  const isTrialing = subscription.status === SubscriptionStatus.TRIALING;

  // Check actual Stripe payment method rather than trialEndBehavior,
  // which is just a configuration for what happens when the trial ends
  const hasPaymentMethod = isTrialing
    ? subscription.rawData
      ? !!subscription.rawData.default_payment_method
      : !!subscription.trialEndBehavior &&
        subscription.trialEndBehavior !== 'cancel'
    : false;

  // Use "Purchase more" if no additional allowance OR usage is greater than the warning threshold
  const shouldShowPurchaseMore = !hasAdditionalCredits || isRunningOutOfCredits;

  let text: string;
  let url: string;

  const style = { padding: 0, height: 'fit-content', lineHeight: 1.2 };

  if (isFreePlan && shouldShowPurchaseMore) {
    // Free plan users should activate subscription to purchase more
    text = 'Activate subscription to purchase more';
    url = AppRoutes.pricing.path;
    return {
      text,
      style,
      url,
      useSubscriptionHandler: true,
    };
  } else if (isTrialing && shouldShowPurchaseMore) {
    if (hasPaymentMethod) {
      // Trial users with payment method can buy credits directly (which will convert their trial)
      text = 'Purchase more credits';
      url = AppRoutes.buyCredits.path;
      return { text, style, url, useSubscriptionHandler: false };
    } else {
      // Trial users without payment method need to activate subscription first
      text = 'Activate subscription to buy more';
      url = AppRoutes.pricing.path;
      return { text, style, url, useSubscriptionHandler: true };
    }
  } else {
    // Regular users - purchase more or manage credits
    text = shouldShowPurchaseMore ? 'Purchase more' : 'Manage credits';
    url = AppRoutes.buyCredits.path;
    return { text, style, url, useSubscriptionHandler: false };
  }
};

interface GetCreditButtonInfoParams {
  ability: Ability;
  balanceInfo: CreditBalance;
  subscriptionInfo: SubscriptionInfo | null;
  currentTeam: Team | null;
}
export const getCreditButtonInfo = ({
  ability,
  balanceInfo,
  subscriptionInfo,
  currentTeam = null,
}: GetCreditButtonInfoParams): CreditButtonInfo => {
  // Extract subscription data from subscriptionInfo
  const subscription = subscriptionInfo?.subscription || null;
  const info = _getCreditButtonInfo(balanceInfo, subscription);

  // Use subscriptionInfo for permission check (contains organization context)
  const canManageSubscription = ability.can(
    'manage',
    Resource.Subscription,
    convertSubscriptionDataToStripeSubscription(subscriptionInfo?.subscription)
  );

  const buttonTooltip = canManageSubscription
    ? undefined
    : 'You do not have permission to manage the subscription';

  const buttonHidden = !canManageSubscription;
  let instructionForSubscription = undefined;

  if (balanceInfo.isRunningOutOfCredits && buttonHidden) {
    if (currentTeam?.master_organization_id) {
      const masterTeamName = currentTeam.master_organization
        ? getTeamName(currentTeam.master_organization)
        : currentTeam.master_organization_id;
      instructionForSubscription = `Contact an account owner of team ${masterTeamName} to add credits`;
    } else {
      instructionForSubscription = 'Contact an account owner to add credits';
    }
  }

  return {
    ...info,
    ability,
    buttonDisabled: !canManageSubscription,
    buttonHidden,
    buttonTooltip,
    instructionForSubscription,
  };
};

export const getCreditDisplayText = (
  currentAccount: TeamUser | PersonalUser | null,
  balanceInfo: CreditBalance,
  opts?: {
    withRenewalDate: boolean;
  }
): string => {
  const {
    usedCredits,
    totalCredits,
    remainingCredits,
    oneTimeCreditsPurchased,
    oneTimeCreditsRemaining,
    nextRenewalDate,
  } = balanceInfo;
  const { withRenewalDate = false } = opts || {};
  const renewalInfo =
    withRenewalDate && nextRenewalDate
      ? ` -- resets on ${formatDate(nextRenewalDate)}`
      : '';

  let masterTeamInfo = '';
  if (currentAccount && 'team' in currentAccount) {
    const teamUserAccount = currentAccount as TeamUser;
    masterTeamInfo = teamUserAccount.team?.master_organization_id
      ? ' on parent team subscription'
      : '';
  }

  if (oneTimeCreditsRemaining > 0) {
    const combinedTotal = totalCredits + oneTimeCreditsPurchased;
    // Derive totalUsed from combinedTotal so the math is always consistent:
    // totalUsed + remainingCredits === combinedTotal
    const totalUsed = combinedTotal - remainingCredits;
    const baseRemaining = Math.max(
      0,
      remainingCredits - oneTimeCreditsRemaining
    );
    return `${totalUsed} (${remainingCredits}/${combinedTotal} left (${baseRemaining} base + ${oneTimeCreditsRemaining} one-time)${masterTeamInfo}${renewalInfo})`;
  }

  return `${usedCredits} (${remainingCredits}/${totalCredits} left${masterTeamInfo}${renewalInfo})`;
};
